import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { Subscription, take } from 'rxjs';

import { Board, Offer, OfferTerm, Reservation, RoomType, Slot } from '../../../../reservation.model';

import { ReservationSummaryCommonService } from '../../../services/reservation-summary-common.service';
import { ReservationSummaryStoreService } from '../../../services/reservation-summary-store.service';

import { AuthService } from '../../../../../auth/auth.service';
import { DateService } from '../../../../../common/date.service';
import { AlertType } from '../../../../../common/enums/AlertType';
import { OfferChangeTypes } from '../../../../../common/enums/OfferChangeType';
import { CancellationStatuses, Status } from '../../../../../common/enums/Status';
import { ReservationService } from '../../../../reservation.service';
import { ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent } from './reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component';
import { ReservationSummaryAccomodationOfferRoomsDialogComponent } from './reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog.component';
import { Permissions } from '@/common/enums/Permissions';
import { ReservationServicesDialogComponent } from './reservation-services-dialog/reservation-services-dialog.component';

const LUGGAGE_SERVICE_TYPE_NAME = 'Bagaż';

@Component({
	selector: 'app-reservation-summary-accommodation-offer-rooms',
	templateUrl: './reservation-summary-accomodation-offer-rooms.component.html',
	styleUrls: ['./reservation-summary-accomodation-offer-rooms.component.scss'],
})
export class ReservationSummaryAccomodationOfferRoomsComponent implements OnDestroy, OnInit {
	@Input() offer: Offer;
	protected readonly Status = Status;

	roomType: RoomType;
	board: Board = null;
	term: OfferTerm = null;
	readonly Permissions = Permissions;

	protected readonly OfferChangeTypes = OfferChangeTypes;
	private _subscription: Subscription = null;
	private reservation: Reservation = null;

	constructor(
		readonly dateService: DateService,
		readonly service: ReservationService,
		protected readonly auth: AuthService,
		private readonly commonService: ReservationSummaryCommonService,
		private readonly dialog: MatDialog,
		private readonly store: ReservationSummaryStoreService,
	) {}

	ngOnInit(): void {
		const { reservation } = this.store.getters();

		this._subscription = reservation().subscribe((reservation: Reservation) => {
			this.reservation = reservation;
			this.board = this.offer?.board;
			if (this.offer) {
				this.term = this.offer?.term;
			}
			this.roomType = this.offer?.roomType;
		});
	}

	ngOnDestroy(): void {
		this._subscription?.unsubscribe();
	}

	get isCanceled(): boolean {
		return CancellationStatuses.includes(this.offer?.status as Status);
	}

	/**
	 * Business case: if there's only one non-cancelled offer, we don't want our sales team to cancel it manually if there's a need to do that. Instead, we want to force them to cancel entire reservation. This makes job for backend team easier in terms on changing statuses
	 */
	get isCancellable(): boolean {
		return (
			this.reservation?.offer.filter((offer) => !CancellationStatuses.includes(offer.status as Status)).length > 1
		);
	}

	showCancelDialog(): void {
		let reservationId = 0;
		const subscription = this.store
			.getters()
			.reservation()
			.subscribe((val) => {
				reservationId = val.id;
			});
		this.commonService.showCancelDialog({
			reservationId: reservationId,
			offerId: this.offer?.id,
			slotId: null,
		});

		subscription.unsubscribe();
	}

	get offerToConfig() {
		return {
			board: {
				id: this.offer.board.id,
				name: this.offer.board.name,
				price: this.offer.slot[0].priceBoard,
				additionalRoomSizeCharge: this.offer.additionalRoomSizeCharge,
			},
			term: this.offer.term.id,
			promotion: this.offer.promotion[0],
			promotionValue: this.offer.totalPriceWithoutPromotion - this.offer.totalPrice,
			promotionCatalog: this.offer.promotion.map((v) => {
				return v.catalog.id;
			})[0],
			dateRange: this.offer.term.dateRange,
			fee: 0,
			fullPrice:
				this.offer.slot[0].catalogPrice + this.offer.slot[0].priceBoard - this.offer.slot[0].promotionValue,
			catalogPrice:
				this.offer.slot[0].catalogPrice -
				this.offer.slot[0].priceTransportDeparture -
				this.offer.slot[0].priceTransportReturn,
			nightsStay: this.reservation.nightsStay,
			product: this.offer.product,
			participants: {
				adults: this.offer.slot.length,
				children: 0,
			},
			roomType: {
				id: this.offer.roomType?.id,
				name: this.offer.roomType?.name,
			},
			transportKind: this.offer.transportKind,
			stop: {
				id: this.offer.slot[0]?.stopDeparture?.stop.id,
				name: this.offer.slot[0]?.stopDeparture?.stop.location.name,
				transportDeparture: this.offer.slot[0].transportDeparture,
				transportReturn: this.offer.slot[0].transportReturn,
			},
			wasTransportAvailable: this.offer.wasTransportAvailable,
			surchargeFee: this.offer.surchargeFee,
			surchargeCurrency: this.offer.surchargeCurrency,
			surchargeForExtraRoomSpace: this.offer.additionalRoomSizeCharge,
			insurance: this.offer.insurance,
			insurer: this.offer.insurer?.id ?? null,
		};
	}

	openChangeOfferDialog(): void {
		const subscription = this.store
			.getters()
			.reservation()
			.subscribe((val) => {
				let oskarFault = false;
				if (val?.alerts.find((v) => v.id === 'COO' || v?.id === 'COF')) oskarFault = true;

				this.dialog
					.open(ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent, {
						width: '1000px',
						disableClose: true,
						data: {
							currentOffer: {
								...this.offer,
								slot: this.cloneSlotsWithLuggageMethodValues(this.offer.slot),
								oskarFault: oskarFault,
								type: OfferChangeTypes.CONFIG,
							},
							selectedOffer: this.offerToConfig,
							surchargeService: null,
							reservationStatus: this.reservation.status,
						},
					})
					.afterOpened()
					.subscribe(() => {});
			});
		subscription.unsubscribe();
	}

	openServicesDialog(): void {
		const originalDateObj = new Date(this.offer.term.dateRange.lower);
		if (!this.offer.roomType?.name) {
			originalDateObj.setDate(originalDateObj.getDate() - 1);
		}
		const convertedLowerDate = originalDateObj.toISOString().split('T')[0];
		const firstSlot = this.offer.slot?.[0];
		const params = new HttpParams()
			.set('date_range', `${convertedLowerDate},${this.offer.term.dateRange.upper}`)
			.set('products', String(this.offer.product.id))
			.set('kinds', String(this.offer.product.productKind.id))
			.appendAll({ transport: [String(this.offer.transportKind), 'A'] })
			.set('stop_id', String(firstSlot?.stopDeparture?.stop?.id ?? ''))
			.set('transport_departure_id', String(firstSlot?.stopDeparture?.transport ?? ''))
			.set('transport_return_id', String(firstSlot?.stopReturn?.transport ?? ''))
			.set('limit', '999');
		this.dialog.open(ReservationServicesDialogComponent, {
			width: '80vw',
			maxWidth: '80vw',
			autoFocus: false,
			data: {
				slots: this.cloneSlotsWithLuggageMethodValues(this.offer.slot),
				params: params,
				nightsStay: this.reservation?.nightsStay,
				offerTotalPrice: this.offer.totalPrice - this.offer.additionalRoomSizeCharge,
			},
		});
	}

	showChangeOfferListDialog(type: string): void {
		this.store
			.getters()
			.reservation()
			.pipe(take(1))
			.subscribe((val) => {
				let oskarFault = false;
				if (
					val?.alerts.find(
						(v) => v.id === AlertType.OBJECT_CANCELLED_BY_OSKAR || v?.id === 'COF', // I can't see this alert inside UML but i don't want to break anything by removing it
					)
				)
					oskarFault = true;
				this.dialog
					.open(ReservationSummaryAccomodationOfferRoomsDialogComponent, {
						minWidth: '1000px',
						disableClose: true,
						data: {
							...this.offer,
							oskarFault: oskarFault,
							type: type,
							reservationStatus: this.reservation?.status,
						},
					})
					.afterOpened()
					.subscribe();
			});
	}

	setOfferIdToChange(): void {
		this.commonService.offerIdToChange.set(this.offer.id);
	}

	private cloneSlotsWithLuggageMethodValues(slots: Slot[]): Slot[] {
		return slots.map((slot) => ({
			...slot,
			slotServices: slot.slotServices.map((slotService) => {
				const isLuggageService = slotService.service?.serviceType?.name === LUGGAGE_SERVICE_TYPE_NAME;

				return {
					...slotService,
					service: {
						...slotService.service,
						methodValue: isLuggageService ? slotService.price : slotService.service.methodValue,
					},
				};
			}),
		}));
	}
}
